import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { SchemaOrg, organizationSchema } from '../../utils/seo';

const jobDetails = [
  {
    label: '職種',
    value: '保険営業スタッフ（正社員）',
    icon: 'ri-briefcase-line',
  },
  {
    label: '仕事内容',
    value: '・個人・法人のお客様への保険提案\n・既存顧客のアフターフォロー\n・新規開拓営業\n・保険金請求手続きのサポート',
    icon: 'ri-file-list-3-line',
  },
  {
    label: '応募資格',
    value: '・高校卒業以上\n・普通自動車免許（AT限定可）\n・未経験者歓迎\n・保険業界経験者優遇',
    icon: 'ri-user-line',
  },
  {
    label: '勤務時間',
    value: '9:00〜18:00（休憩1時間）\n残業：月平均10時間程度',
    icon: 'ri-time-line',
  },
  {
    label: '給与',
    value: '月給20万円〜35万円\n※経験・能力により優遇\n※各種手当含む',
    icon: 'ri-money-dollar-circle-line',
  },
  {
    label: '福利厚生',
    value: '・社会保険完備\n・交通費支給（月2万円まで）\n・資格取得支援制度\n・年次有給休暇',
    icon: 'ri-heart-line',
  },
  {
    label: '休日',
    value: '週休2日制（日曜・祝日）\n年間休日105日\n夏季・年末年始休暇あり',
    icon: 'ri-calendar-line',
  },
];

const workEnvironment = [
  {
    title: '充実の研修制度',
    description: '保険の基礎から営業スキルまで、丁寧な研修でサポート。資格取得費用は会社負担です。',
    image: 'https://public.readdy.ai/ai/img_res/541ae8ea809af4fe5552381292eb5aba.jpg',
  },
  {
    title: 'ワークライフバランス',
    description: '残業は月平均10時間程度。プライベートの時間も大切にできる働きやすい環境です。',
    image: 'https://readdy.ai/api/search-image?query=Happy%20Japanese%20office%20workers%20enjoying%20break%20time%2C%20smiling%20colleagues%20in%20modern%20office%2C%20relaxed%20friendly%20atmosphere%2C%20natural%20lighting%2C%20authentic%20Japanese%20people%2C%20simple%20clean%20background&width=600&height=400&seq=worklife-001&orientation=landscape',
  },
  {
    title: '地域貢献',
    description: '地域のイベントや活動に積極的に参加。お客様との距離が近い、やりがいのある仕事です。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20business%20people%20participating%20in%20local%20community%20event%2C%20volunteers%20helping%20at%20outdoor%20festival%2C%20friendly%20atmosphere%2C%20natural%20lighting%2C%20authentic%20Japanese%20people%2C%20simple%20clean%20background&width=600&height=400&seq=community-001&orientation=landscape',
  },
];

export default function CareersPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jobPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: '保険営業スタッフ（正社員）',
    description: '個人・法人のお客様への保険提案、既存顧客のアフターフォロー、新規開拓営業、保険金請求手続きのサポート',
    datePosted: '2025-01-15',
    validThrough: '2025-12-31',
    employmentType: 'FULL_TIME',
    hiringOrganization: organizationSchema,
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '○○町1-2-3',
        addressLocality: '岡山市北区',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'JPY',
      value: {
        '@type': 'QuantitativeValue',
        minValue: 200000,
        maxValue: 350000,
        unitText: 'MONTH',
      },
    },
    qualifications: '高校卒業以上、普通自動車免許（AT限定可）、未経験者歓迎、保険業界経験者優遇',
    workHours: '9:00〜18:00（休憩1時間）',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '求人情報',
        item: `${siteUrl}/careers`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <SchemaOrg type="JobPosting" data={jobPostingSchema} />
      <SchemaOrg type="WebPage" data={breadcrumbSchema} />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center overflow-hidden" style={{ height: '40vh' }}>
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20environment%20with%20young%20Japanese%20professionals%20working%20together%2C%20collaborative%20workspace%2C%20bright%20natural%20lighting%2C%20professional%20atmosphere%2C%20authentic%20Japanese%20people%20in%20business%20casual%20attire%2C%20simple%20clean%20background&width=1920&height=800&seq=careers-hero-001&orientation=landscape"
              alt="オフィス環境"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">求人情報</h1>
            <p className="text-lg md:text-xl mb-3 md:mb-4">一緒に岡山の未来を守りませんか</p>
            <nav className="text-sm">
              <Link to="/" className="hover:text-[#FF9B7A] transition-colors cursor-pointer">ホーム</Link>
              <span className="mx-2">&gt;</span>
              <span>求人情報</span>
            </nav>
          </div>
        </section>

        {/* Job Description */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-12 md:mb-16">
              <div className="h-48 md:h-64 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Bright%20modern%20office%20interior%20with%20Japanese%20staff%20working%20at%20desks%2C%20professional%20workspace%2C%20natural%20lighting%2C%20clean%20contemporary%20design%2C%20authentic%20Japanese%20employees%2C%20simple%20clean%20background&width=1200&height=400&seq=office-interior-001&orientation=landscape"
                  alt="オフィス内観"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div className="bg-[#FF9B7A] text-white px-4 py-2 rounded-full text-sm font-bold">
                    正社員
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#2C3E50]">
                    保険営業スタッフ
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {jobDetails.map((detail, index) => (
                    <div key={index} className="flex items-start gap-3 md:gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FF9B7A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${detail.icon} text-lg md:text-xl text-[#FF9B7A]`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-[#2C3E50] mb-2 text-sm md:text-base">{detail.label}</h3>
                        <p className="text-sm md:text-base text-gray-700 whitespace-pre-line leading-relaxed">
                          {detail.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Work Environment */}
            <div className="mb-12 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-[#2C3E50] text-center mb-8 md:mb-12">
                働く環境
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {workEnvironment.map((env, index) => (
                  <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                    <div className="h-48 overflow-hidden">
                      <img
                        src={env.image}
                        alt={env.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg md:text-xl font-bold text-[#2C3E50] mb-3">
                        {env.title}
                      </h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        {env.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Application CTA */}
            <div className="bg-[#FF9B7A] rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4 px-4">
                まずはカジュアル面談から
              </h2>
              <p className="text-white/90 text-sm sm:text-base md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">
                保険業界未経験の方も大歓迎！あなたの新たな挑戦をサポートします。
                まずはお気軽にお話しを聞かせてください。
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto bg-white text-[#FF9B7A] px-8 md:px-10 py-4 rounded-full text-base md:text-lg font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                >
                  応募する
                </Link>
                <a
                  href="tel:0860123456"
                  className="w-full sm:w-auto bg-transparent text-white border-2 border-white px-8 md:px-10 py-4 rounded-full text-base md:text-lg font-bold hover:bg-white hover:text-[#FF9B7A] transition-colors whitespace-nowrap cursor-pointer"
                >
                  お電話で相談
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}